/**
 * errorMonitor.js
 *
 * Central error monitoring for the React app.
 * All errors — API failures, JS runtime errors, broken assets, CSP violations,
 * unhandled promise rejections, and component crashes — flow through logError().
 *
 * Errors are collected in a queue and sent as a batch to the logging backend
 * every 5 seconds, or immediately when 10 errors accumulate.
 *
 * Payload shape sent to the backend:
 *   { session_id, logs: [{ PK, SK, summary, details }, ...] }
 *
 * Required env variables:
 *   REACT_APP_ERROR_LOG_URL     — base URL of the logging backend (host only)
 *   REACT_APP_ERROR_LOG_API_KEY — API key sent in X-API-KEY header
 */

import React from 'react';
import HttpStatus from '../helpers/status.enum';

// ─── Error type and severity constants ────────────────────────────────────────

export const ErrorType = Object.freeze({
  API_ERROR:            'API_ERROR',
  NETWORK_ERROR:        'NETWORK_ERROR',
  TIMEOUT_ERROR:        'TIMEOUT_ERROR',
  AUTHENTICATION_ERROR: 'AUTHENTICATION_ERROR',
  VALIDATION_ERROR:     'VALIDATION_ERROR',
  SERVER_ERROR:         'SERVER_ERROR',
  COMPONENT_ERROR:      'COMPONENT_ERROR',
  RUNTIME_ERROR:        'RUNTIME_ERROR',
  PROMISE_REJECTION:    'PROMISE_REJECTION',
  IMAGE_LOAD_ERROR:     'IMAGE_LOAD_ERROR',
  ASSET_LOAD_ERROR:     'ASSET_LOAD_ERROR',
  UNKNOWN_ERROR:        'UNKNOWN_ERROR',
});

export const Severity = Object.freeze({
  CRITICAL: 'critical',
  ERROR:    'error',
  WARNING:  'warning',
  INFO:     'info',
});

// ─── Session ID ───────────────────────────────────────────────────────────────
// Persisted in localStorage so errors from the same browser tab are groupable.

const getSessionId = () => {
  try {
    let id = localStorage.getItem('hg_session_id');
    if (!id) {
      id = crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      localStorage.setItem('hg_session_id', id);
    }
    return id;
  } catch (_) { return 'unknown'; }
};

// ─── Route slug extraction ────────────────────────────────────────────────────
// Reads listing and booking slugs from the URL path so the DynamoDB PK can
// be scoped to a specific listing or booking rather than falling back to session.
// Reserved path segments that are not slugs are filtered out.

const RESERVED_SEGMENTS     = ['terms', 'privacy'];
const RESERVED_SUB_SEGMENTS = ['guestready'];

const getRouteSlugs = () => {
  try {
    const [listing, booking] = window.location.pathname.split('/').filter(Boolean);
    if (!listing || RESERVED_SEGMENTS.includes(listing.toLowerCase())) {
      // Not on a listing page — fall back to whatever was last stored
      return { listing_slug: localStorage.getItem('listingSlug') || null, booking_slug: null };
    }
    return {
      listing_slug: listing,
      booking_slug: booking && !RESERVED_SUB_SEGMENTS.includes(booking.toLowerCase()) ? booking : null,
    };
  } catch (_) {
    return { listing_slug: null, booking_slug: null };
  }
};

// ─── Device info ──────────────────────────────────────────────────────────────
// Raw browser API values — no UA string parsing so nothing is guessed.

const getDeviceInfo = () => {
  try {
    return {
      user_agent: navigator.userAgent || null,
      platform:   navigator.userAgentData?.platform || navigator.platform || null,
      mobile:     navigator.userAgentData?.mobile ?? null,
      screen:     `${window.screen.width}x${window.screen.height}`,
      viewport:   `${window.innerWidth}x${window.innerHeight}`,
      language:   navigator.language || null,
      timezone:   Intl.DateTimeFormat().resolvedOptions().timeZone || null,
    };
  } catch (_) {
    return { user_agent: navigator?.userAgent || 'unknown' };
  }
};

// ─── Network info ─────────────────────────────────────────────────────────────

const getNetworkInfo = () => {
  try {
    const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    return {
      online:         navigator.onLine,
      effective_type: conn?.effectiveType || null,
    };
  } catch (_) {
    return { online: navigator?.onLine ?? null };
  }
};

// ─── Sensitive data sanitizer ─────────────────────────────────────────────────
// Walks request/response objects and replaces values whose keys match known
// sensitive field names with '[REDACTED]' before the payload leaves the browser.

const SENSITIVE = [
  'password', 'token', 'access_token', 'refresh_token', 'authorization',
  'api_key', 'apikey', 'cookie', 'secret', 'credit_card', 'cardnumber',
  'card_number', 'cardnum', 'cvv', 'cvc', 'ssn', 'passport', 'otp',
  'security_code', 'pin_code', 'pincode',
];
const SENSITIVE_EXACT = ['auth', 'session', 'card', 'pin'];

const isSensitiveKey = (key) => {
  const k = String(key).toLowerCase();
  return SENSITIVE.some(s => k.includes(s)) || SENSITIVE_EXACT.includes(k);
};

const sanitize = (obj, depth = 0) => {
  if (depth > 5 || obj == null || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(i => sanitize(i, depth + 1));
  return Object.fromEntries(
    Object.entries(obj).map(([k, v]) => [k, isSensitiveKey(k) ? '[REDACTED]' : sanitize(v, depth + 1)])
  );
};

const buildApiInfo = (apiInfo) => {
  if (!apiInfo) return null;
  const clean = sanitize(apiInfo);
  return { ...clean, request_body: clean.request_body, response_body: clean.response_body };
};

// ─── DynamoDB key builders ────────────────────────────────────────────────────
// PK is scoped to listing or booking so logs can be filtered by property.
// SK is timestamp to keep items sortable and unique.

const buildPK = (listingSlug, bookingSlug, date) => {
  if (listingSlug) return `LISTING#${listingSlug}#${date}`;
  if (bookingSlug) return `BOOKING#${bookingSlug}#${date}`;
  return `SESSION#${getSessionId()}#${date}`;
};

// Summary is a pipe-delimited string for quick scanning in DynamoDB:
// environment | listing_slug | booking_slug | error_type | severity | message
const buildSummary = (parts) =>
  parts.map((p) => {
    if (p == null || p === '') return '-';
    return String(p).replace(/\|/g, '/');
  }).join('|');

// ─── Batch queue ──────────────────────────────────────────────────────────────
// Errors are queued and flushed as a single API call either every 5 seconds
// or when the queue reaches 10 items — whichever comes first.
// On page hide (tab close / navigate away) we flush immediately using keepalive
// so the last batch is not lost.

const LOG_URL    = `${process.env.REACT_APP_ERROR_LOG_URL}/api/logs/log-client-error`;
const BATCH_SIZE = 10;
const _queue     = [];

const flushQueue = () => {
  if (_queue.length === 0 || !LOG_URL) return;
  const batch = _queue.splice(0);
  try {
    fetch(LOG_URL, {
      method:    'POST',
      headers:   { 'Content-Type': 'application/json', 'X-API-KEY': process.env.REACT_APP_ERROR_LOG_API_KEY || '' },
      body:      JSON.stringify({ session_id: getSessionId(), log_type: 'GUEST', logs: batch }),
      keepalive: true, // survives page unload
    }).catch(() => {});
  } catch (_) {}
};

setInterval(flushQueue, 15000);
window.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') flushQueue();
});

const sendLog = (payload) => {
  try {
    _queue.push(payload);
    if (_queue.length >= BATCH_SIZE) flushQueue();
  } catch (_) {}
};

// ─── Core log function ────────────────────────────────────────────────────────
// Single entry point for all error logging across the app.
// Called directly for API and component errors; also called internally by
// the global handlers below for runtime JS errors, asset failures, etc.

export const logError = ({
  message   = 'Unknown error',
  type      = ErrorType.UNKNOWN_ERROR,
  severity  = Severity.ERROR,
  component = null,  // { name, component_stack } — set by ErrorBoundary
  apiInfo   = null,  // { url, method, status, request_body, response_body, ... }
} = {}) => {
  try {
    const { listing_slug, booking_slug } = getRouteSlugs();
    const environment   = process.env.REACT_APP_CUSTOM_NODE_ENV || 'unknown';
    const timestamp     = new Date().toISOString();

    sendLog({
      PK:      buildPK(listing_slug, booking_slug, timestamp.slice(0, 10)),
      SK:      timestamp,
      summary: buildSummary([environment, listing_slug, booking_slug, type, severity, message]),
      details: {
        environment,
        listing_slug,
        booking_slug,
        error_type: type,
        severity,
        message:    String(message),
        page:       { url: window.location.href, route: window.location.pathname },
        device:     getDeviceInfo(),
        network:    getNetworkInfo(),
        api:        buildApiInfo(apiInfo),
        component:  component || null,
      },
    });
  } catch (_) {}
};

// ─── API error classifier ─────────────────────────────────────────────────────
// Maps HTTP status codes to ErrorType + Severity.
// Used by fetch-wrapper so it does not need to know the mapping itself.

export const classifyApiError = (status) => {
  if (!status || status === 0)                                                                    return { type: ErrorType.NETWORK_ERROR,        severity: Severity.ERROR    };
  if (status === HttpStatus.HTTP_UNAUTHORIZED)                                                    return { type: ErrorType.AUTHENTICATION_ERROR,  severity: Severity.WARNING  };
  if (status === HttpStatus.HTTP_FORBIDDEN)                                                       return { type: ErrorType.AUTHENTICATION_ERROR,  severity: Severity.WARNING  };
  if (status === HttpStatus.HTTP_REQUEST_TIMEOUT || status === HttpStatus.HTTP_GATEWAY_TIMEOUT)    return { type: ErrorType.TIMEOUT_ERROR,         severity: Severity.ERROR    };
  if (status === HttpStatus.HTTP_BAD_REQUEST || status === HttpStatus.HTTP_UNPROCESSABLE_ENTITY)  return { type: ErrorType.VALIDATION_ERROR,      severity: Severity.WARNING  };
  if (status >= HttpStatus.HTTP_INTERNAL_SERVER_ERROR)                                            return { type: ErrorType.SERVER_ERROR,          severity: Severity.CRITICAL };
  return                                                                                                 { type: ErrorType.API_ERROR,             severity: Severity.ERROR    };
};

// ─── Global error handlers ────────────────────────────────────────────────────
// Must be called once at app startup (in index.js) before the first render
// so that no early errors are missed.
//
// Covers four categories the app code does not handle explicitly:
//   1. Runtime JS errors (window.onerror)
//   2. Unhandled promise rejections (window.onunhandledrejection)
//   3. Broken image / script / stylesheet loads (document error event, capture phase)
//   4. Resources silently blocked by the browser with no DOM event
//      (PerformanceObserver watching for transferSize=0 + very short duration)
//   5. Content Security Policy violations (securitypolicyviolation event)

export const initGlobalErrorHandlers = () => {
  // 1. Runtime JS errors — browser extensions are excluded to avoid noise
  window.onerror = (message, source, lineno, colno) => {
    if (source?.includes('extension://')) return false;
    logError({
      message:  `${String(message)} (${source}:${lineno}:${colno})`,
      type:     ErrorType.RUNTIME_ERROR,
      severity: Severity.ERROR,
    });
    return false;
  };

  // 2. Unhandled promise rejections
  window.onunhandledrejection = (event) => {
    const err = event.reason instanceof Error ? event.reason : null;
    logError({
      message:  err?.message || String(event.reason) || 'Unhandled promise rejection',
      type:     ErrorType.PROMISE_REJECTION,
      severity: Severity.ERROR,
    });
  };

  // 3. Broken asset loads — capture phase so the event reaches us before it stops bubbling
  document.addEventListener('error', (event) => {
    const el  = event.target;
    const tag = el?.tagName?.toUpperCase();
    if (!el || el === window || !['IMG', 'SCRIPT', 'LINK'].includes(tag)) return;
    const url = el.src || el.href || 'unknown';
    if (url.startsWith('data:')) return; // inline data URIs are not real failures
    logError({
      message:  `Failed to load ${tag.toLowerCase()}: ${url}`,
      type:     tag === 'IMG' ? ErrorType.IMAGE_LOAD_ERROR : ErrorType.ASSET_LOAD_ERROR,
      severity: Severity.WARNING,
    });
  }, true);

  // 5. CSP violations — fired when the browser blocks a resource or inline script
  document.addEventListener('securitypolicyviolation', (event) => {
    logError({
      message:  `Blocked by CSP: ${event.blockedURI} (${event.violatedDirective})`,
      type:     ErrorType.ASSET_LOAD_ERROR,
      severity: Severity.WARNING,
    });
  });
};

// ─── React Error Boundary ─────────────────────────────────────────────────────
// Catches errors thrown during render / lifecycle of any child component.
// React requires a class component for this — _ErrorBoundaryCore is kept
// private (unexported) and wrapped in a functional component so the rest of
// the codebase stays consistent with the functional-component pattern.
// On crash the boundary renders null (no fallback UI) and logs the error.

class _ErrorBoundaryCore extends React.Component {
  state = { hasError: false };
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(error, info) {
    logError({
      message:   error.message || 'Component render error',
      type:      ErrorType.COMPONENT_ERROR,
      severity:  Severity.CRITICAL,
      component: { name: this.props.name || 'Unknown', component_stack: info?.componentStack || null },
    });
  }
  render() { return this.state.hasError ? null : this.props.children; }
}

export const ErrorBoundary = ({ name, children }) => (
  <_ErrorBoundaryCore name={name}>{children}</_ErrorBoundaryCore>
);
